import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { CUSTOMER_FLAG_CODES, didNumberSchema, ivrPinSchema } from '@app/shared';
import type { CustomerDto, CustomerFlags, DidNumberInput, IvrPinInput } from '@app/shared';
import { FormDialog } from '../../components/dialogs';
import { TextInput, applyServerErrors } from '../../components/form';
import { useNotify } from '../../components/NotifyProvider';
import { api } from '../../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { ReleasedFeature, useFeatureReleased } from '../../release';
import { Can } from '../auth/guards';
import { useUpdateDid, useUpdateFlags, useUpdateIvrPin } from './api';
import { Field, yesNo } from './parts';

/** The three switches after the contact switches, with the reference's labels and codes. */
const FLAGS: Array<[keyof CustomerFlags, string]> = [
  ['disableCreditCard', 'Disable Credit Card'],
  ['doNotSellInfo', 'Do not sell info'],
  ['deleteData', 'Delete Data'],
  ['charity', 'Charity'],
];

/**
 * Disable Credit Card (PC810), Do not sell info (PC846), Delete Data (PC847). Each saves as
 * soon as it is moved; without its code it is shown as a plain value.
 */
export function FlagSwitches({ customer }: { customer: CustomerDto }) {
  const { can } = useAuth();
  const notify = useNotify();
  const update = useUpdateFlags(customer.id);
  const featureOn = useFeatureReleased();
  const toggle = async (key: keyof CustomerFlags, value: boolean) => {
    try {
      await update.mutateAsync({ [key]: value });
      notify.success('Customer information updated.');
    } catch (err) {
      notify.error(err);
    }
  };
  return (
    <>
      {FLAGS.filter(([key]) => key !== 'charity' || featureOn('account-type-filters')).map(
        ([key, label]) => {
          const code = CUSTOMER_FLAG_CODES[key];
          return can(code) ? (
            <FormControlLabel
              key={key}
              label={`${label} (${code})`}
              control={
                <Switch
                  size="small"
                  checked={customer.flags[key]}
                  disabled={update.isPending}
                  onChange={(e) => void toggle(key, e.target.checked)}
                />
              }
            />
          ) : (
            <Field key={key} label={`${label} (${code})`} value={yesNo(customer.flags[key])} />
          );
        },
      )}
    </>
  );
}

/** "PIN" with the reference's "Update IVR PIN" dialog ("IVR PIN (4 digits)", Update). */
export function PinValue({ customer }: { customer: CustomerDto }) {
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const update = useUpdateIvrPin(customer.id);
  const { control, handleSubmit, reset, setError } = useForm<IvrPinInput>({
    resolver: zodResolver(ivrPinSchema),
    defaultValues: { pin: '' },
  });
  const submit = handleSubmit(async (values) => {
    try {
      await update.mutateAsync(values);
      notify.success('IVR PIN updated.');
      setOpen(false);
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  return (
    <>
      {customer.pin}
      <ReleasedFeature name="update-did-ivr-pin">
        <Can code="PC377">
          <Button
            size="small"
            sx={{ ml: 1 }}
            onClick={() => {
              reset({ pin: '' });
              setOpen(true);
            }}
          >
            Update IVR PIN
          </Button>
        </Can>
      </ReleasedFeature>
      <FormDialog
        open={open}
        title="Update IVR PIN"
        onClose={() => setOpen(false)}
        onSubmit={(e) => void submit(e)}
        submitting={update.isPending}
        submitLabel="Update"
        maxWidth="xs"
      >
        <Box sx={{ display: 'grid', pt: 1 }}>
          <TextInput
            control={control}
            name="pin"
            label="IVR PIN (4 digits)"
            required
            inputMode="numeric"
            slotProps={{ htmlInput: { maxLength: 4 } }}
          />
        </Box>
      </FormDialog>
    </>
  );
}

/** "DID No" with the reference's "Update DID" link and its "DID Number" dialog (Save). */
export function DidValue({ customer }: { customer: CustomerDto }) {
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const update = useUpdateDid(customer.id);
  const { control, handleSubmit, reset, setError } = useForm<DidNumberInput>({
    resolver: zodResolver(didNumberSchema),
    defaultValues: { didNumber: customer.didNumber },
  });
  const submit = handleSubmit(async (values) => {
    try {
      await update.mutateAsync(values);
      notify.success('DID number saved.');
      setOpen(false);
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  return (
    <>
      {customer.didNumber || 'N/A'}
      <ReleasedFeature name="update-did-ivr-pin">
        <Can code="PC377">
          <Button
            size="small"
            sx={{ ml: 1 }}
            onClick={() => {
              reset({ didNumber: customer.didNumber });
              setOpen(true);
            }}
          >
            Update DID
          </Button>
        </Can>
      </ReleasedFeature>
      <FormDialog
        open={open}
        title="DID Number"
        onClose={() => setOpen(false)}
        onSubmit={(e) => void submit(e)}
        submitting={update.isPending}
        submitLabel="Save"
        maxWidth="xs"
      >
        <Box sx={{ display: 'grid', pt: 1 }}>
          <TextInput
            control={control}
            name="didNumber"
            label="DID Number"
            inputMode="numeric"
            slotProps={{ htmlInput: { maxLength: 10 } }}
          />
        </Box>
      </FormDialog>
    </>
  );
}

/**
 * Gives the customer a temporary password for the customer portal, for a customer who cannot get
 * one by e-mail or phone. It is shown once, to the staff member who is helping them.
 */
export function PortalPassword({ customer }: { customer: CustomerDto }) {
  const notify = useNotify();
  const [answer, setAnswer] = useState<{ temporaryPassword: string; portalUrl: string } | null>(
    null,
  );
  const give = useMutation({
    mutationFn: () =>
      api.post<{ temporaryPassword: string; portalUrl: string }>(
        `/customers/${customer.id}/portal-password`,
      ),
    onSuccess: setAnswer,
    onError: (err) => notify.error(err),
  });
  return (
    <Can code="PC377">
      <Button variant="outlined" disabled={give.isPending} onClick={() => give.mutate()}>
        Portal password
      </Button>
      <Dialog open={Boolean(answer)} onClose={() => setAnswer(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Portal password</DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>
            Give this to the customer. It works once, for one hour, and is shown only now.
          </Typography>
          <Typography aria-label="Temporary password" sx={{ fontWeight: 700, mb: 1 }}>
            {answer?.temporaryPassword}
          </Typography>
          <Typography variant="body2">They sign in at {answer?.portalUrl}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnswer(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Can>
  );
}
